import { HTTPErrorType, SortDirection } from 'dy-frontend-http-repository/lib/data/enums';
import { HTTPErrorResponse, SortData } from 'dy-frontend-http-repository/lib/data/types';
import { TimeTrackingEntrySortField } from 'dy-frontend-http-repository/lib/modules/CoreUser/inputs';
import { TaskTimeTrackingEntryResource } from 'dy-frontend-http-repository/lib/modules/CoreUser/resources';
import { useStore } from 'effector-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Flex from '@app/components/Flex';
import Grid from '@app/components/Grid';
import Heading from '@app/components/Heading';
import NonIdealState from '@app/components/NonIdealState';
import Pagination from '@app/components/Pagination';
import RouterLink from '@app/components/RouterLink';
import Select from '@app/components/Select';
import DevText from '@app/components/Text';
import TaskStateTag from '@app/containers/components/TaskStateTag';
import { Endpoints } from '@app/data/consts';
import { ToastUtils } from '@app/data/utils';
import { usePageTitle, useScrollToTop } from '@app/hooks';
import { Card, Colors, Divider, Icon, Intent, MenuItem, Spinner } from '@blueprintjs/core';
import { ItemRendererProps } from '@blueprintjs/select';

import { $coreUser } from '../../store/states';

import { fetchTaskTimeTrackingEntries } from './store/effects';
import { resetTaskTimeTrackingEntries } from './store/events';
import { $taskTimeTrackingEntries } from './store/states';

import { amountOfTaskTimeTrackingEntries, timeTrackingSortItemsInformation } from './consts';

const CoreUserTaskTimeTracking: React.FC = () => {
    const { scrollToTop } = useScrollToTop();
    const { changeTitle } = usePageTitle('Member Time Tracking');
    const navigate = useNavigate();

    const coreUser = useStore($coreUser);
    const taskTimeTrackingEntries = useStore($taskTimeTrackingEntries);
    const isTaskTimeTrackingEntriesFetching = useStore(fetchTaskTimeTrackingEntries.pending);

    const [page, setPage] = useState(1);
    const [sortData, setSortData] = useState<SortData<TimeTrackingEntrySortField>>({
        field: 'last_entry_at',
        direction: 'asc',
    });

    const handleLoadPage = async (newPage: number) => {
        if (!coreUser) return;

        const pageOffset = (newPage - 1) * amountOfTaskTimeTrackingEntries;

        try {
            await fetchTaskTimeTrackingEntries({
                id: coreUser.id,
                pagination: {
                    _pagination: { limit: amountOfTaskTimeTrackingEntries, offset: pageOffset },
                    _sort: sortData,
                },
            });
        } catch (e) {
            // Log
            console.error(e);

            const response = (e as any).response as HTTPErrorResponse;
            if (response.data.type === HTTPErrorType.MISSING) {
                // Show error message
                ToastUtils.showToast({
                    message: `Task time tracking entries for member with ID ${coreUser.id} was not found`,
                    intent: Intent.DANGER,
                });

                // Go to member tasks page
                navigate(Endpoints.CORE_USER_TASKS.replace(':coreUserId', coreUser.id));
            }
        }
    };

    useEffect(() => {
        handleLoadPage(page);

        // Scroll to top
        scrollToTop();

        // eslint-disable-next-line
    }, [page, sortData]);

    useEffect(() => {
        if (!coreUser) {
            return;
        }

        changeTitle(`Member Time Tracking - ${coreUser.first_name} ${coreUser.last_name}`);
    }, [coreUser?.id]);

    useEffect(() => {
        return () => {
            resetTaskTimeTrackingEntries();
        };
    }, []);

    if (!coreUser) {
        return null;
    }

    const getDurationInHoursAndMinutesFormat = (duration: number) => {
        const hours = Math.trunc(duration / 60);
        const minutes = duration - 60 * hours;

        return { hours: `${hours}`, minutes: `${minutes}`.padStart(2, '0') };
    };

    const renderTimeTrackingSortingSelect = () => {
        const currentLabel =
            timeTrackingSortItemsInformation[`${sortData.field}${sortData.direction}`] ??
            `${sortData.field} - ${sortData.direction}`;

        const renderItem = (item: SortData<TimeTrackingEntrySortField>, { handleClick }: ItemRendererProps) => {
            const isMenuItemActive = item.direction === sortData.direction && item.field === sortData.field;
            const label =
                timeTrackingSortItemsInformation[`${item.field}${item.direction}`] ??
                `${item.field} - ${item.direction}`;

            return (
                <MenuItem
                    active={isMenuItemActive}
                    key={`${item.field}${item.direction}`}
                    text={label}
                    onClick={handleClick}
                />
            );
        };

        const handleItemSelect = (item: SortData<TimeTrackingEntrySortField>) => {
            setSortData(item);
        };

        const getItems = (): SortData<TimeTrackingEntrySortField>[] => {
            return [
                { field: 'last_entry_at', direction: SortDirection.ASC },
                { field: 'last_entry_at', direction: SortDirection.DESC },
                { field: 'state_latest_transition_at', direction: SortDirection.ASC },
                { field: 'state_latest_transition_at', direction: SortDirection.DESC },
                { field: 'total_duration_min', direction: SortDirection.ASC },
                { field: 'total_duration_min', direction: SortDirection.DESC },
            ];
        };

        return (
            <Select<SortData<TimeTrackingEntrySortField>>
                items={getItems()}
                itemRenderer={renderItem}
                onItemSelect={handleItemSelect}
                popoverProps={{ matchTargetWidth: true, usePortal: false, className: 'ml-auto' }}
                selectButtonProps={{
                    rightIcon: 'double-caret-vertical',
                    icon: 'sort',
                    text: currentLabel,
                    style: { width: '300px' },
                }}
            />
        );
    };

    const renderSingleTaskTimeTrackingEntry = (entry: TaskTimeTrackingEntryResource, index: number) => {
        const { task, total_duration_min } = entry;

        const { hours, minutes } = getDurationInHoursAndMinutesFormat(total_duration_min);

        return (
            <Grid className="mb-2" xs={6} lg={6} key={task.id}>
                <Card compact style={{ height: '100%' }}>
                    <Flex fullHeight direction="column" justify="space-between" align="stretch">
                        <div>
                            <TaskStateTag large={false} withCircle={false} className="mb-small" state={task.state} />

                            <Heading className="mb-small" type="h4" style={{ wordBreak: 'break-all' }}>
                                <RouterLink
                                    color="inherit"
                                    to={Endpoints.TASK_WORK_REPORTS.replace(':taskId', task.id)}
                                >
                                    {task.title}
                                </RouterLink>
                            </Heading>

                            <DevText inline muted className="mr-2">
                                <Icon className="mr-1" icon="new-grid-item" color={Colors.GRAY2} />
                                <RouterLink
                                    color="inherit"
                                    to={Endpoints.TASK_CATEGORY.replace(':taskCategoryId', task.category.id)}
                                >
                                    {task.category.title}
                                </RouterLink>
                            </DevText>
                        </div>

                        <div>
                            <Divider className="mt-2 mb-1" />

                            <DevText>
                                <DevText muted inline>
                                    Total time spent:
                                </DevText>{' '}
                                {hours}h {minutes}min
                            </DevText>
                        </div>
                    </Flex>
                </Card>
            </Grid>
        );
    };

    const renderTaskTimeTracking = () => {
        // Time tracking entries were NOT fetched yet
        if (!taskTimeTrackingEntries) {
            return (
                <Flex justify="center">
                    <Spinner />
                </Flex>
            );
        }

        // Time tracking entries were fetched, but where are no time tracking entries existed for this core user
        if (taskTimeTrackingEntries.items.length === 0) {
            return (
                <NonIdealState
                    icon={<Icon className="mb-2" icon="search" size={70} />}
                    title={
                        <Heading type="h4" className="mb-1">
                            No request time tracking entries were found
                        </Heading>
                    }
                />
            );
        }

        // TODO: mockup for the task time tracking card
        return <Grid container>{taskTimeTrackingEntries.items.map(renderSingleTaskTimeTrackingEntry)}</Grid>;
    };

    const renderPagination = () => {
        // Time tracking entries is not fetched yet
        if (taskTimeTrackingEntries === null) {
            return null;
        }

        // Check if offset paginator exist
        if (taskTimeTrackingEntries.paginator === null) {
            return;
        }

        // Only one page exist
        if (!taskTimeTrackingEntries.paginator.has_more && taskTimeTrackingEntries.paginator.offset === 0) {
            return null;
        }

        return (
            <Flex justify="flex-end">
                <Pagination
                    fetching={isTaskTimeTrackingEntriesFetching}
                    hasMore={taskTimeTrackingEntries.paginator.has_more}
                    className="mt-2"
                    page={page}
                    amountOfItemsOnPage={amountOfTaskTimeTrackingEntries}
                    totalItems={taskTimeTrackingEntries.paginator.total}
                    onPageChange={(newPage) => setPage(newPage)}
                />
            </Flex>
        );
    };

    return (
        <div>
            <Flex className="mb-2" direction="row" justify="space-between">
                <Heading type="h3" className="mr-2">
                    Time tracking
                </Heading>
                {renderTimeTrackingSortingSelect()}
            </Flex>

            {/* Time tracking entries list */}
            {renderTaskTimeTracking()}

            {/* Pagination */}
            {renderPagination()}
        </div>
    );
};

export default CoreUserTaskTimeTracking;
